"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@workspace/ui/lib/utils";
import type { ReactNode } from "react";
import { MagnusLogo } from "./magnus-logo";

/**
 * How {@link FloatingChatTrigger} is drawn. `"solid"` is the filled FAB in
 * the theme's foreground colour; `"bezel"` is a matte, crafted double frame: an outer hairline ring, a
 * gap of a second surface tone, then an inner bordered tile holding the
 * Magnus logo exactly as the app chrome draws it (`MagnusLogo`,
 * `text-magnus`).
 */
export type FloatingChatTriggerAppearance = "solid" | "bezel";

/*
 * Bezel: two concentric frames in two surface tones. The outer face sits a
 * step off the page, the inner tile is the page itself, so the pair inverts
 * with the theme on its own — a grey bezel round a white tile on light, a
 * charcoal one round a black tile on dark. The top highlight is the `--glass`
 * token's white at a strength each theme can carry.
 */
const BEZEL_ROOT = cn("border-0 text-muted-foreground hover:text-foreground", "bg-[color-mix(in_oklab,var(--foreground)_6%,var(--background))] hover:bg-[color-mix(in_oklab,var(--foreground)_6%,var(--background))] dark:bg-[color-mix(in_oklab,var(--foreground)_10%,var(--background))] dark:hover:bg-[color-mix(in_oklab,var(--foreground)_10%,var(--background))]", "[--b-hi:rgb(from_var(--glass)_r_g_b_/_0.9)] dark:[--b-hi:rgb(from_var(--glass)_r_g_b_/_0.07)]", "[--b-shade:color-mix(in_oklab,var(--hero-deep)_16%,transparent)] dark:[--b-shade:color-mix(in_oklab,var(--background)_85%,transparent)]", "shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--foreground)_13%,transparent),inset_0_1px_0_var(--b-hi),0_1px_2px_var(--b-shade),0_8px_18px_-8px_var(--b-shade)]", "hover:scale-100 active:scale-[0.97]", "focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background", "motion-reduce:transition-none motion-reduce:active:scale-100");
const BEZEL_TILE = cn("pointer-events-none absolute inset-1 rounded-full bg-background", "bg-[linear-gradient(180deg,color-mix(in_oklab,var(--foreground)_4%,transparent),transparent_55%)]", "shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--foreground)_15%,transparent),inset_0_1px_0_var(--b-hi)]", "transition-shadow duration-200 group-hover/button:shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--foreground)_32%,transparent),inset_0_1px_0_var(--b-hi)]", "motion-reduce:transition-none");

/**
 * Surface, hover/press and focus styling per appearance. Position, size and
 * transitions are the trigger's, shared by every appearance.
 */
export const TRIGGER_APPEARANCE_CLASS: Record<FloatingChatTriggerAppearance, string> = {
  // Borderless and inverted against the page: a white button with a dark
  // logo on dark, a black one with a white logo on light.
  solid: cn("border-0 bg-foreground text-background shadow-lg", "hover:scale-110 hover:bg-foreground/90", "active:scale-[0.96]", "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"),
  bezel: BEZEL_ROOT
};

/**
 * The trigger's content: `children`, or the Magnus logo when none are given,
 * laid on the bezel's inner tile when the appearance has one.
 */
export function TriggerFace(t0) {
  const $ = _c(6);
  const {
    appearance,
    children
  } = t0;
  const bezel = appearance === "bezel";
  let t1;
  if ($[0] !== bezel || $[1] !== children) {
    t1 = children ?? <MagnusLogo className={cn("size-8", bezel && "text-magnus")} />;
    $[0] = bezel;
    $[1] = children;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const mark = t1;
  if (!bezel) {
    return mark;
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span aria-hidden="true" className={BEZEL_TILE} />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== mark) {
    t3 = <>{t2}<span className="relative flex">{mark}</span></>;
    $[4] = mark;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}